import { BetStatus, BOT_CONDITIONS_MAP_REVERSE, BOT_OPTIONS_MAP_REVERSE, WL } from '@/constants';
import { AppError } from '@/errors/appError';
import { AbstractBet } from '@/models/abstractBet';
import { IBetDataAPI, IBotConditionsParsed } from '@/types';

export class BetItem extends AbstractBet {
    public attacksAway: number | null;
    public attacksHome: number | null;
    public balanceAgentPc: string | null;
    public balanceBookmakerAc: string | null;
    public balanceBookmakerPc: string | null;
    public balancePc: string | null;
    public companyCommission: string | null;
    public conditions: string | null;
    public dangerousAttacksAway: number | null;
    public dangerousAttacksHome: number | null;
    public history: string | null;
    public oddsReal: string;
    public payCommission: string | null;
    public positionPercAgent: number;
    public positionPercAgentRequest: number;
    public positionPercCompany: number;
    public positionPercCompanyRequest: number;
    public positionPlatformRuleId: number | null;
    public positionUserRuleId: number | null;
    public possessionTimeAway: number | null;
    public possessionTimeHome: number | null;
    public realStakeBookmakerAc: string;
    public redAway: number | null;
    public redHome: number | null;
    public ref: string | null;
    public result: WL | null;
    public resultBookmaker: WL | null;
    public shotsAway: number | null;
    public shotsHome: number | null;
    public shotsOnTargetAway: number | null;
    public shotsOnTargetHome: number | null;
    public stakeAgentPc: string;
    public stakeBookmakerAc: string;
    public stakePositionAgentPc: string;
    public stakePositionCompanyPc: string;
    public stakeRequestPc: string;
    public stakeValidLimitAc: string;
    public statusId: BetStatus;
    public statusIdBookmaker: BetStatus;
    public validStakeBookmakerPc: string;

    constructor(data: IBetDataAPI) {
        super(data);

        this.balancePc = data.balance_pc;
        this.statusId = data.status_id;
        this.ref = data.ref;
        this.oddsReal = data.odds_real;
        this.redHome = data.red_home;
        this.redAway = data.red_away;
        this.attacksHome = data.attacks_home;
        this.attacksAway = data.attacks_away;
        this.dangerousAttacksHome = data.dangerous_attacks_home;
        this.dangerousAttacksAway = data.dangerous_attacks_away;
        this.possessionTimeHome = data.possession_time_home;
        this.possessionTimeAway = data.possession_time_away;
        this.shotsHome = data.shots_home;
        this.shotsAway = data.shots_away;
        this.shotsOnTargetHome = data.shots_on_target_home ?? null;
        this.shotsOnTargetAway = data.shots_on_target_away ?? null;
        this.conditions = data.conditions ?? null;
        this.history = data.history ?? null;
        this.stakeRequestPc = data.stake_request_pc;
        this.stakeBookmakerAc = data.stake_bookmaker_ac;
        this.realStakeBookmakerAc = data.real_stake_bookmaker_ac;
        this.stakeAgentPc = data.stake_agent_pc;
        this.stakePositionAgentPc = data.stake_position_agent_pc;
        this.stakePositionCompanyPc = data.stake_position_company_pc;
        this.validStakeBookmakerPc = data.valid_stake_bookmaker_pc;
        this.stakeValidLimitAc = data.stake_valid_limit_ac;
        this.balanceAgentPc = data.balance_agent_pc;
        this.balanceBookmakerPc = data.balance_bookmaker_pc;
        this.balanceBookmakerAc = data.balance_bookmaker_ac;
        this.companyCommission = data.company_com;
        this.payCommission = data.pay_com;
        this.positionPercAgent = data.position_perc_agent;
        this.positionPercAgentRequest = data.position_perc_agent_request;
        this.positionPercCompany = data.position_perc_company;
        this.positionPercCompanyRequest = data.position_perc_company_request;
        this.positionPlatformRuleId = data.position_platform_rule_id;
        this.positionUserRuleId = data.position_user_rule_id;
        this.statusIdBookmaker = data.status_id_bookmaker as BetStatus;
        this.result = data.result;
        this.resultBookmaker = data.result_bookmaker;
    }

    get agentTotal(): string | null {
        if (this.balanceAgentPc == null || this.payCommission == null) {
            return null;
        }
        return (Number(this.balanceAgentPc) + Number(this.payCommission)).toString();
    }

    get companyBalance(): string | null {
        if (this.balanceBookmakerPc == null || this.balanceAgentPc == null) {
            return null;
        }
        return (Number(this.balanceBookmakerPc) - Number(this.balanceAgentPc)).toString();
    }

    get companyPl(): string | null {
        if (this.companyBalance == null || this.companyCommission == null || this.payCommission == null) {
            return null;
        }
        return (Number(this.companyBalance) + Number(this.companyCommission) - Number(this.payCommission)).toString();
    }

    get conditionsParsed(): IBotConditionsParsed {
        return this._lazyLoad('conditionsParsed', this.conditions, () => {
            try {
                const parsed = JSON.parse(this.conditions || '{}');
                const normalized: any = {
                    bot: {
                        settings: {},
                    },
                };

                if (parsed.bot) {
                    Object.keys(parsed.bot).forEach((shortKey) => {
                        if (shortKey === 'sts') {
                            if (parsed.bot.sts) {
                                Object.keys(parsed.bot.sts).forEach((settingsShortKey) => {
                                    const settingsFullKey = BOT_CONDITIONS_MAP_REVERSE[settingsShortKey] ?? settingsShortKey;
                                    normalized.bot.settings[settingsFullKey] = parsed.bot.sts[settingsShortKey];
                                });
                            }
                        } else {
                            const fullKey = BOT_OPTIONS_MAP_REVERSE[shortKey] ?? shortKey;
                            normalized.bot[fullKey] = parsed.bot[shortKey];
                        }
                    });
                }

                if (parsed.agc) normalized.ageCategory = parsed.agc;
                if (parsed.cat) normalized.category = parsed.cat;
                if (parsed.lcg) normalized.limitsCategory = parsed.lcg;
                if (parsed.clg) normalized.customLeague = parsed.clg;
                if (parsed.clx) normalized.customLeagueExclude = parsed.clx;

                return normalized;
            } catch (e) {
                throw new AppError('Invalid bot condition JSON:', { cause: e });
            }
        });
    }

    get isBet(): boolean {
        return true;
    }

    get isFail(): boolean {
        return false;
    }

    get missingStake(): number {
        return Number(this.stakeRequestPc) - Number(this.stakePc);
    }
}
